import { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { API_BASE_URL } from "../../services/apiClient";
import { LoginError, clasificarFalloLogin } from "../../utils/loginErrors";
import { tokenTurnstileVencido, TURNSTILE_RENOVAR_MS } from "../../utils/turnstileToken";
import { reportarFalloLogin } from "../../config/sentry";
import { limpiarCorreoLogin } from "../../utils/loginCredenciales";

// Subset del BoundTurnstileObject que exponemos hacia afuera para llamar
// `.reset()`. Lo capturamos en `onSuccess` y lo guardamos en una ref para
// poder regenerar el token después de cada submit (los tokens de Turnstile
// son single-use; reusarlos rebota con `timeout-or-duplicate`).
type TurnstileBound = { reset: () => void };

export interface LoginFormData {
  email: string;
  password: string;
}

export interface LoginFormErrors {
  email?: string;
  password?: string;
  general?: string;
  turnstile?: string;
}

export const useLoginLogic = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  // Counter que se incrementa para forzar re-mount limpio del componente
  // <Turnstile> vía la prop `key`. `bound.reset()` de react-turnstile@1.1.4
  // tiene un bug donde el iframe interno queda desincronizado ("Form
  // submission canceled because the form is not connected"), generando
  // tokens huérfanos que CF rebota con `invalid-input-response`. Cambiar
  // `key` deja a React desmontar el iframe completo y montar uno nuevo
  // desde cero, evitando el bug. Round 2026-04-26 fase 8.
  const [turnstileKey, setTurnstileKey] = useState<number>(0);

  // Ref al objeto bound del widget Turnstile. `onSuccess` nos lo entrega
  // junto con el token recién generado. La usamos para resetear el widget
  // después de cada submit fallido — sino el frontend reintenta con el
  // mismo token, Cloudflare lo marca como `timeout-or-duplicate`, y el
  // backend devuelve 400 "Verificación de seguridad fallida". Round
  // 2026-04-26 fase 8 (UX fix — login intermitente).
  const turnstileBoundRef = useRef<TurnstileBound | null>(null);

  // Timestamp (ms) del último token recibido. Lo usamos para detectar
  // tokens viejos antes del submit y regenerarlos. Los tokens de Turnstile
  // tienen TTL de 300s; si el usuario abrió la página y se distrajo,
  // `refreshExpired="auto"` debería refrescar pero no siempre dispara
  // (tabs en background, throttling). Round 2026-04-26 fase 8 — mitigación
  // de `invalid-input-response`.
  const tokenIssuedAtRef = useRef<number>(0);

  // Resolver de la promesa que espera el próximo token cuando hacemos
  // un reset pre-submit. Lo dispara `handleTurnstileSuccess`.
  const pendingTokenResolverRef = useRef<((t: string) => void) | null>(null);

  // Renovación PROACTIVA del token (round 2026-10-28). `refreshExpired="auto"` y `onExpire`
  // dependen de timers del navegador; en la app instalada del celular el sistema suspende la
  // app al salir de ella (copiar la clave desde otra app, el gestor de contraseñas) y al volver
  // el token ya venció sin que el widget se entere: el login salía rechazado con la pantalla
  // mostrando «Operación exitosa». Al volver a primer plano, y cada 20 s mientras esté
  // visible, miramos la edad del token y pedimos uno nuevo ANTES de que el usuario lo gaste.
  useEffect(() => {
    const revisar = () => {
      if (document.visibilityState === "hidden") return;
      if (tokenTurnstileVencido(tokenIssuedAtRef.current, Date.now())) {
        tokenIssuedAtRef.current = 0;
        setTurnstileToken(null);
        setTurnstileKey((k) => k + 1);
      }
    };
    document.addEventListener("visibilitychange", revisar);
    window.addEventListener("focus", revisar);
    window.addEventListener("pageshow", revisar);
    const id = window.setInterval(revisar, 20_000);
    return () => {
      document.removeEventListener("visibilitychange", revisar);
      window.removeEventListener("focus", revisar);
      window.removeEventListener("pageshow", revisar);
      window.clearInterval(id);
    };
  }, []);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user types
    if (errors[name as keyof LoginFormErrors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRememberMe(e.target.checked);
  };

  const validateForm = (): boolean => {
    const newErrors: LoginFormErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = "El email es requerido";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "El formato de email es inválido";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "La contraseña es requerida";
    } else if (formData.password.length < 6) {
      newErrors.password = "La contraseña debe tener al menos 6 caracteres";
    }

    // Turnstile validation
    if (!turnstileToken) {
      newErrors.turnstile = "Por favor, completa la verificación de seguridad";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // CRÍTICO: estos callbacks usan `useCallback` con deps `[]` y functional
  // updates de setState. Si tuvieran `[errors]` como dep, cada cambio de
  // errores (al tipear email/password, al limpiar el toast, etc.) recrearía
  // la identidad de la función → React detectaría cambio de prop en
  // `<Turnstile>` → la lib re-monta el iframe → el token en vuelo se
  // invalida. Síntoma: login intermitente "4 fallan, 5to pasa".
  // Round 2026-04-26 fase 8 — bug encontrado vía "Form submission canceled
  // because the form is not connected" en logs del iframe.
  const handleTurnstileSuccess = useCallback(
    (
      token: string,
      _preClearance?: boolean,
      bound?: TurnstileBound,
    ) => {
      // (Round 2026-10-28: se quitó el log de depuración que imprimía en la consola el largo y los
      // primeros 20 caracteres de cada token de Turnstile. Era temporal desde el round 2026-04-26 y un
      // token es una credencial de un solo uso: no se escribe en consola.)
      setTurnstileToken(token);
      tokenIssuedAtRef.current = Date.now();
      // Capturamos el bound para poder llamar `.reset()` después de cada
      // intento de login. El callback de react-turnstile lo entrega en
      // cada generación de token nuevo.
      if (bound) turnstileBoundRef.current = bound;

      // Si alguien estaba esperando un token fresh (reset pre-submit),
      // notificarlo. Round 2026-04-26 fase 8.
      if (pendingTokenResolverRef.current) {
        pendingTokenResolverRef.current(token);
        pendingTokenResolverRef.current = null;
      }

      // Functional update — no dependemos de `errors` en closure, así que
      // la identidad de esta callback NO cambia entre renders.
      setErrors((prev) =>
        prev.turnstile ? { ...prev, turnstile: undefined } : prev,
      );
    },
    [],
  );

  const handleTurnstileError = useCallback((error?: unknown) => {
    setErrors((prev) => ({
      ...prev,
      turnstile:
        "Error en la verificación de seguridad. Por favor, inténtalo de nuevo.",
    }));
    setTurnstileToken(null);
    tokenIssuedAtRef.current = 0;
    // Round 2026-10-28: el widget mismo falló (no es el servidor). Cloudflare entrega un código
    // numérico (110xxx = clave/dominio mal configurados, 300/600xxx = el desafío no se pudo
    // completar) que antes se perdía. Va a Sentry con display-mode para ver si pasa solo en la
    // app instalada. El código es un número de Cloudflare, no un dato del usuario.
    reportarFalloLogin(
      { kind: "turnstile_fallo", code: `widget:${String(error ?? "desconocido").slice(0, 40)}` },
      { apiBase: API_BASE_URL },
    );
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
    tokenIssuedAtRef.current = 0;
    // Forzamos remount del widget completo cambiando la `key`. NO usamos
    // `bound.reset()` porque el reset interno del lib v1.1.4 produce
    // tokens huérfanos (ver comentario en `turnstileKey`).
    setTurnstileKey((k) => k + 1);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Si el token tiene más de 240s, regenerarlo antes de enviar. Los
      // tokens Turnstile tienen TTL de 300s y `refreshExpired="auto"` no
      // siempre dispara a tiempo. Cubrimos el caso "usuario abrió /login,
      // se distrajo, volvió a los 5 min y dio submit" — con el token viejo
      // CF responde `invalid-input-response`. Round 2026-04-26 fase 8.
      const ageMs = tokenIssuedAtRef.current
        ? Date.now() - tokenIssuedAtRef.current
        : Infinity;
      let tk = turnstileToken ?? "";
      if (!tk || ageMs > TURNSTILE_RENOVAR_MS) {
        console.warn(
          `[login] Token Turnstile vacío o viejo (age=${ageMs}ms). Regenerando antes de enviar.`,
        );
        tk = await new Promise<string>((resolve) => {
          const timer = setTimeout(() => resolve(""), 10_000);
          pendingTokenResolverRef.current = (t) => {
            clearTimeout(timer);
            resolve(t);
          };
          // Re-mount del widget vía `key` (no `bound.reset()`).
          setTurnstileKey((k) => k + 1);
        });
        if (!tk) {
          const fallo = clasificarFalloLogin({ status: 400, body: { code: "TURNSTILE_UNAVAILABLE" } });
          reportarFalloLogin(fallo, { apiBase: API_BASE_URL });
          setErrors({
            general:
              "No se pudo verificar la seguridad a tiempo. Revisa tu conexión e inténtalo de nuevo (si sigue igual, recarga la página).",
          });
          return;
        }
      }

      // Enviar el turnstileToken al backend — el widget client-side es
      // defense-in-depth, la validación real ocurre server-side. Round
      // 2026-04-26 fase 3 (P1-C). validateForm() ya garantiza que el token
      // existe (rechazo client-side si falta), pero el backend re-valida.
      const response = await api.authApi.login({
        // Se recorta el correo: el teclado del celular (autocompletado, sugerencias) suele
        // dejar un espacio al final y eso es un «usuario inexistente». La contraseña NO se
        // toca: un espacio puede ser parte de ella.
        email: limpiarCorreoLogin(formData.email),
        password: formData.password,
        turnstileToken: tk,
      });

      const { token, user } = response;

      // Actualizar contexto: login maneja la persistencia del `user`
      // cacheado según rememberMe. El JWT vive en cookie HttpOnly + memoria
      // del context — ya no se persiste en storage. Round 2026-04-26 fase 6.
      login(token, user, rememberMe);

      // Redirigir al usuario a la página de inicio apropiada según su rol.
      // Los aseadores no tienen dashboard en /home (ven reservas/ocupación que
      // no les aplican), así que caen directo en /aseos — la única ruta con
      // trabajo operativo para ellos.
      const rol = String(user?.rol ?? "").toLowerCase();
      navigate(rol === "aseador" ? "/aseos" : "/home");
    } catch (error) {
      // `authApi.login` lanza un LoginError con la causa ya clasificada (round 2026-10-28):
      // cada causa tiene su mensaje y se reporta a Sentry con el modo de visualización.
      // Cualquier otra excepción (p. ej. un fallo dentro de login()/navigate()) se trata como
      // desconocida para no mostrar un mensaje de credenciales que no corresponde.
      const fallo =
        error instanceof LoginError
          ? error.failure
          : clasificarFalloLogin({ status: 0, body: null });
      if (!(error instanceof LoginError)) console.error("Error en inicio de sesión:", error);
      reportarFalloLogin(fallo, { apiBase: API_BASE_URL });
      setErrors({ general: fallo.message });
      // Falló el intento — el token de Turnstile YA fue consumido por
      // Cloudflare al hacer siteverify. Si el usuario reintenta sin
      // resetear el widget, mandaremos el mismo token y Cloudflare lo
      // rechaza con `timeout-or-duplicate`. Forzamos la regeneración
      // re-mountando el componente vía `key` — NO `bound.reset()` porque
      // el reset interno de react-turnstile@1.1.4 deja al iframe en un
      // estado inconsistente ("Form submission canceled because the
      // form is not connected") y los tokens nuevos quedan huérfanos
      // → CF rebota con `invalid-input-response` por 3-4 intentos
      // hasta que el widget se "asienta". Round 2026-04-26 fase 8.
      setTurnstileKey((k) => k + 1);
      setTurnstileToken(null);
      tokenIssuedAtRef.current = 0;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    errors,
    isLoading,
    rememberMe,
    turnstileToken,
    turnstileKey,
    handleChange,
    handleCheckboxChange,
    handleSubmit,
    handleTurnstileSuccess,
    handleTurnstileError,
    handleTurnstileExpire,
  };
};
